/**
 * The meeting rooms a booking can use (SC-422). A company's rooms come from its `ColyseusRoom`
 * rows; each room's map owns its VRs, and only VRs with `kind === 'meeting'` are bookable. A room
 * accepts external guests only when its map has a reception (rule 5), which Cloud Code checks too.
 */

import Parse from '../../services/initializeParse'
import ColyseusRoomService from '../../services/back4app/ColyseusRoomService'
import MapService from '../../services/back4app/MapService'
import type { ColyseusRoomRecord } from '../../../../types/Maps'

export interface BookableRoomOption {
  /** The VR id, which bookings store as `virtualRoomId`. */
  roomId: string
  name: string
  description?: string
  customRoomId: string
  /** The Colyseus room's name, shown as a group on the dashboard. */
  groupName?: string
}

export interface BookableRooms {
  options: BookableRoomOption[]
  customRoomIdsWithReception: Set<string>
}

interface EnabledMap {
  mapId: string
  hasReception: boolean
}

interface MeetingVirtualRoom {
  virtualRoomId: string
  name: string
  description?: string
  mapId: string
}

export interface BookableRoomsApi {
  listCompanyRooms(companyId: string): Promise<ColyseusRoomRecord[]>
  listEnabledMaps(): Promise<EnabledMap[]>
  findMeetingVirtualRooms(mapIds: string[]): Promise<MeetingVirtualRoom[]>
}

export const parseBookableRoomsApi: BookableRoomsApi = {
  // The services memoise for 60 s and share concurrent calls, so opening the form again, or the
  // menu and the form together, does not repeat the two list queries.
  listCompanyRooms: (companyId) => ColyseusRoomService.list(companyId),
  listEnabledMaps: () => MapService.listEnabled(),
  async findMeetingVirtualRooms(mapIds) {
    const query = new Parse.Query('VirtualRoom')
    query.containedIn('mapId', mapIds)
    query.equalTo('kind', 'meeting')
    query.select(['virtualRoomId', 'name', 'metadata', 'mapId'])
    query.limit(1000)
    const rows = await query.find()
    return rows.map((r: { get: (key: string) => any }) => ({
      virtualRoomId: r.get('virtualRoomId'),
      name: r.get('name') || r.get('virtualRoomId'),
      description: r.get('metadata')?.description,
      mapId: r.get('mapId'),
    }))
  },
}

const byGroupThenName = (a: BookableRoomOption, b: BookableRoomOption) =>
  (a.groupName || '').localeCompare(b.groupName || '') || a.name.localeCompare(b.name, undefined, { numeric: true })

/**
 * In a game room (`customRoomId` set) only that room's meeting VRs; on the dashboard, every one
 * of the company's rooms, grouped by room. Sorted by group first so MUI's `groupBy` sees each
 * group's options together.
 */
export async function loadBookableRooms(
  { companyId, customRoomId }: { companyId: string; customRoomId?: string },
  api: BookableRoomsApi = parseBookableRoomsApi
): Promise<BookableRooms> {
  const companyRooms = (await api.listCompanyRooms(companyId)) || []
  const rooms = customRoomId ? companyRooms.filter((r) => r.customRoomId === customRoomId) : companyRooms
  if (rooms.length === 0) return { options: [], customRoomIdsWithReception: new Set() }

  const [maps, virtualRooms] = await Promise.all([
    api.listEnabledMaps(),
    api.findMeetingVirtualRooms([...new Set(rooms.map((r) => r.mapId))]),
  ])
  const receptionMaps = new Set((maps || []).filter((m) => m.hasReception).map((m) => m.mapId))

  const options = rooms
    .flatMap((room) =>
      virtualRooms
        .filter((vr) => vr.mapId === room.mapId)
        .map((vr) => ({
          roomId: vr.virtualRoomId,
          name: vr.name,
          description: vr.description,
          customRoomId: room.customRoomId,
          groupName: customRoomId ? undefined : room.name,
        }))
    )
    .sort(byGroupThenName)

  return {
    options,
    customRoomIdsWithReception: new Set(rooms.filter((r) => receptionMaps.has(r.mapId)).map((r) => r.customRoomId)),
  }
}
